/**
 * Authentication API client.
 *
 * Backend integration for user authentication operations including login,
 * registration, logout, and token verification. Uses cookie-based authentication
 * with HTTP-only cookies for secure token storage.
 *
 * @module authApi
 */

import { API_BASE_URL, getAuthHeaders } from "./apiConfig";
import { apiPost, apiDelete } from "./apiUtils";
import { handleApiError } from "./apiErrorHandler";
import { logger } from "./logger";
import type {
  User,
  LoginResult,
  RegisterResult,
  ChangePasswordResult,
} from '@/types/api/auth';

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Type guard to check if data is an object-like structure
 */
function isUserLike(data: unknown): data is Record<string, unknown> {
  return typeof data === 'object' && data !== null;
}

/**
 * Normalizes user object from backend to ensure consistent id property.
 * 
 * The backend may send user data with either 'id' or 'userId' (camelCase alias) field.
 * This function normalizes the user object to always use 'id' for frontend consistency.
 * 
 * **When to use:**
 * - When receiving user data directly from API responses (outside of authApi functions)
 * - When transforming user data from external sources
 * - In API client functions that return user data
 * 
 * **When NOT to use:**
 * - Components should use `useAuth()` hook instead
 * - Hooks should use `useAuth()` from @/hooks/useAuth which provides normalized user data
 * - The `verifyToken()` function already uses this internally
 * 
 * @param userData - Raw user data from backend (may have 'id' or 'userId' field)
 * @returns Normalized User object with 'id' property, or null if userData is invalid
 * 
 * @example
 * ```typescript
 * // In an API function
 * const response = await fetch('/api/user');
 * const data = await response.json();
 * const normalizedUser = normalizeUser(data.user);
 * ```
 * 
 * @example
 * ```typescript
 * // DON'T do this in components - use hooks instead:
 * // const user = normalizeUser(someUserData); // ❌
 * 
 * // DO this instead:
 * // const { user } = useAuth(); // ✅
 * ```
 */
export function normalizeUser(userData: unknown): User | null {
  if (!isUserLike(userData)) return null;

  // Extract id from either id or userId property
  const userId = (typeof userData.id === 'string' ? userData.id : null) ||
    (typeof userData.userId === 'string' ? userData.userId : null) ||
    "";

  // Create normalized user object with id always set
  // Remove userId property if it exists separately to avoid duplication
  const { userId: _, ...rest } = userData;
  const normalized: User = {
    ...rest,
    id: userId,
  } as User;

  return normalized;
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Authenticate user with email and password.
 *
 * Logs in a user with their email and password. Uses direct fetch (instead of
 * apiUtils) to access response headers for rate limiting information (Retry-After).
 * Sets authentication cookie on success.
 *
 * @param email - User's email address
 * @param password - User's password
 * @returns Promise resolving to LoginResult object containing:
 *   - `success`: Boolean indicating if login succeeded
 *   - `message`: Error message if login failed
 *   - `code`: Error code if login failed
 *   - `retryAfter`: Seconds to wait before retrying (if rate limited)
 *   - `status`: HTTP status code
 *
 * @example
 * ```typescript
 * const result = await login('user@example.com', 'password123');
 * if (result.success) {
 *   // User is now authenticated
 * } else {
 *   console.error(result.message);
 * }
 * ```
 */
export async function login(
  email: string,
  password: string,
): Promise<LoginResult> {
  // Note: Using fetch directly to access response headers for retryAfter handling
  // This is a special case that requires header access not available in apiUtils
  try {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: "POST",
      headers: getAuthHeaders(),
      credentials: "include",
      body: JSON.stringify({ email, password }),
    });

    const data = await response.json().catch(() => null);

    if (!response.ok) {
      const retryAfterHeader = response.headers.get("Retry-After");
      const retryAfter =
        data?.retryAfter ??
        (retryAfterHeader ? Number(retryAfterHeader) : undefined);
      const message =
        data?.error || data?.message || "Login failed. Please try again.";
      const code = data?.code;
      handleApiError(
        { status: response.status, message } as Error & { status?: number },
        message,
        "login",
      );
      return {
        success: false,
        message,
        code,
        retryAfter,
        status: response.status,
      };
    }

    if (data?.success) {
      return { success: true };
    } else {
      const message = data?.error || "Invalid email or password";
      logger.error("Login failed", undefined, { message });
      return {
        success: false,
        message,
        code: data?.code,
        status: response.status,
      };
    }
  } catch (error) {
    handleApiError(error, "Unable to connect to server. Please try again later.", "login");
    return {
      success: false,
      message: "Unable to connect to server. Please try again later.",
      status: 0,
    };
  }
}

/**
 * Authenticate (or auto-provision) a user with a Google ID token.
 *
 * Sends the Google Identity Services credential to the backend, which verifies
 * it, links/creates the matching user in our database, and sets the same
 * HttpOnly auth cookie as password login. Mirrors {@link login}'s error and
 * rate-limit handling.
 *
 * @param idToken - The Google ID token (credential) from the GIS button
 * @returns Promise resolving to LoginResult (same shape as {@link login})
 */
export async function loginWithGoogle(idToken: string): Promise<LoginResult> {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/google`, {
      method: "POST",
      headers: getAuthHeaders(),
      credentials: "include",
      body: JSON.stringify({ id_token: idToken }),
    });

    const data = await response.json().catch(() => null);

    if (!response.ok) {
      const retryAfterHeader = response.headers.get("Retry-After");
      const retryAfter =
        data?.retryAfter ??
        (retryAfterHeader ? Number(retryAfterHeader) : undefined);
      const message =
        data?.error || data?.message || "Google sign-in failed. Please try again.";
      const code = data?.code;
      handleApiError(
        { status: response.status, message } as Error & { status?: number },
        message,
        "loginWithGoogle",
      );
      return {
        success: false,
        message,
        code,
        retryAfter,
        status: response.status,
      };
    }

    if (data?.success) {
      return { success: true };
    } else {
      const message = data?.error || "Google sign-in failed";
      logger.error("Google sign-in failed", undefined, { message });
      return {
        success: false,
        message,
        code: data?.code,
        status: response.status,
      };
    }
  } catch (error) {
    handleApiError(error, "Unable to connect to server. Please try again later.", "loginWithGoogle");
    return {
      success: false,
      message: "Unable to connect to server. Please try again later.",
      status: 0,
    };
  }
}

/**
 * Register a new user account.
 *
 * Creates a new user account with email, password, username, and activation code.
 * The activation code is required for account creation.
 *
 * @param email - User's email address
 * @param password - User's password
 * @param username - Desired username (3-50 characters, alphanumeric with underscores/hyphens)
 * @returns Promise resolving to RegisterResult object containing:
 *   - `success`: Boolean indicating if registration succeeded
 *   - `error`: Error message if registration failed
 *
 * @example
 * ```typescript
 * const result = await register(
 *   'user@example.com',
 *   'password123',
 *   'username',
 *   true,
 *   false,
 * );
 * if (result.success) {
 *   // Account created successfully
 * }
 * ```
 */
export async function register(
  email: string,
  password: string,
  username: string,
  dataProcessingConsent: boolean,
  voiceRecordingConsent: boolean,
): Promise<RegisterResult> {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/register`, {
      method: "POST",
      headers: getAuthHeaders(),
      credentials: "include",
      body: JSON.stringify({
        email,
        password,
        username,
        data_processing_consent: dataProcessingConsent,
        voice_recording_consent: voiceRecordingConsent,
      }),
    });

    const data = await response.json().catch(() => null);

    if (!response.ok) {
      const error =
        data?.error || data?.message || "Registration failed. Please try again.";
      logger.error("Registration failed", undefined, {
        status: response.status,
        error,
      });
      return { success: false, error };
    }

    if (data?.success) {
      return { success: true };
    }

    return {
      success: false,
      error: data?.error || data?.message || "Registration failed. Please try again.",
    };
  } catch (error) {
    logger.error("Registration error", error);
    return {
      success: false,
      error: "Unable to connect to server. Please try again later.",
    };
  }
}

/**
 * Log out the current user.
 *
 * Invalidates the authentication session on the server. Failures are handled
 * silently (no error toasts) to avoid disrupting the user experience during logout.
 *
 * @returns Promise that resolves when logout request completes (regardless of success/failure)
 *
 * @example
 * ```typescript
 * await logout();
 * // User is now logged out (even if request failed)
 * ```
 */
export async function logout(): Promise<void> {
  // Silent failure - don't show error toasts for logout
  await apiPost(
    "/auth/logout",
    undefined,
    {
      errorMessage: "Logout failed",
      context: "logout",
      suppressErrorToast: true,
      skipSessionInvalidation: true,
    }
  );
}

/**
 * Verify the current authentication token.
 *
 * Checks if the current user's authentication token (stored in HTTP-only cookie)
 * is valid and retrieves the current user's information. Used to check if a user
 * is still logged in and to get user data on app load.
 *
 * @returns Promise resolving to User object if token is valid, or null if invalid/expired
 *
 * @example
 * ```typescript
 * const user = await verifyToken();
 * if (user) {
 *   console.log(`Logged in as ${user.username}`);
 * } else {
 *   // User is not authenticated
 * }
 * ```
 */
export async function verifyToken(): Promise<User | null> {
  // Use direct fetch to avoid apiGet's error handling which shows a toast on 401.
  // A 401 during token verification is expected for unauthenticated users on public pages.
  try {
    const response = await fetch(`${API_BASE_URL}/auth/verify`, {
      method: 'GET',
      headers: getAuthHeaders(),
      credentials: 'include',
    });

    if (!response.ok) {
      // 401 is expected for unauthenticated users - return null silently
      if (response.status === 401) {
        return null;
      }
      // Log other errors but don't show toast
      logger.warn("Token verification failed", { status: response.status });
      return null;
    }

    const data = await response.json();
    if (!data?.user) {
      return null;
    }

    return normalizeUser(data.user);
  } catch (error) {
    // Network errors or other issues - log but don't show toast
    logger.debug("Token verification error (likely not logged in)", error);
    return null;
  }
}

/**
 * Change the user's password.
 *
 * @param oldPassword - Current password
 * @param newPassword - New password
 * @returns Promise resolving to ChangePasswordResult
 */
/**
 * Permanently deletes the authenticated user's account and all associated data.
 * The backend clears the session cookie on success, so no separate logout is needed.
 *
 * @returns Promise resolving to `{ success: true }` on success, or null on error.
 */
export async function deleteAccount(): Promise<{ success: boolean; message?: string } | null> {
  return apiDelete<{ success: boolean; message?: string }>("/users/me", {
    errorMessage: "Failed to delete account",
    context: "deleteAccount",
  });
}

export async function changePassword(
  oldPassword: string,
  newPassword: string,
): Promise<ChangePasswordResult> {
  const result = await apiPost<{ success: boolean; message?: string }>(
    "/auth/change-password",
    { old_password: oldPassword, new_password: newPassword },
    {
      errorMessage: "Failed to change password",
      context: "changePassword",
    }
  );

  if (!result) {
    return { success: false, message: "Connection error" };
  }

  return {
    success: result.success,
    message: result.message,
  };
}
